/**
 * Timezone detection utilities for display schedules
 * Maps US cities and states to IANA timezone identifiers
 */

// State-level timezone defaults (most common timezone per state)
export const STATE_TIMEZONE_DEFAULTS: Record<string, string> = {
  // Pacific Time
  'CA': 'America/Los_Angeles',
  'WA': 'America/Los_Angeles', 
  'OR': 'America/Los_Angeles',
  'NV': 'America/Los_Angeles',
  
  // Mountain Time
  'MT': 'America/Denver',
  'WY': 'America/Denver',
  'CO': 'America/Denver',
  'NM': 'America/Denver',
  'UT': 'America/Denver',
  'ID': 'America/Denver',
  
  // Arizona (no DST)
  'AZ': 'America/Phoenix',
  
  // Central Time
  'TX': 'America/Chicago',
  'OK': 'America/Chicago',
  'KS': 'America/Chicago',
  'NE': 'America/Chicago',
  'SD': 'America/Chicago',
  'ND': 'America/Chicago',
  'MN': 'America/Chicago',
  'IA': 'America/Chicago',
  'MO': 'America/Chicago',
  'AR': 'America/Chicago',
  'LA': 'America/Chicago',
  'MS': 'America/Chicago',
  'AL': 'America/Chicago',
  'TN': 'America/Chicago',
  'KY': 'America/Chicago',
  'WI': 'America/Chicago',
  'IL': 'America/Chicago',
  'IN': 'America/Indiana/Indianapolis',
  
  // Eastern Time
  'MI': 'America/Detroit',
  'OH': 'America/New_York',
  'WV': 'America/New_York',
  'VA': 'America/New_York',
  'NC': 'America/New_York',
  'SC': 'America/New_York',
  'GA': 'America/New_York',
  'FL': 'America/New_York',
  'PA': 'America/New_York',
  'NY': 'America/New_York',
  'VT': 'America/New_York',
  'NH': 'America/New_York',
  'ME': 'America/New_York',
  'MA': 'America/New_York',
  'RI': 'America/New_York',
  'CT': 'America/New_York',
  'NJ': 'America/New_York',
  'DE': 'America/New_York',
  'MD': 'America/New_York',
  'DC': 'America/New_York',
  
  // Alaska
  'AK': 'America/Anchorage',
  
  // Hawaii
  'HI': 'Pacific/Honolulu',
};

// Special city mappings that override state defaults
export const CITY_STATE_TIMEZONE_MAP: Record<string, string> = {
  // Eastern Indiana cities that use Eastern Time
  'evansville, in': 'America/New_York',
  'louisville, ky': 'America/New_York', // Some parts use Eastern
  
  // Florida cities
  'pensacola, fl': 'America/Chicago', // Florida panhandle uses Central
  'tallahassee, fl': 'America/New_York',
  'jacksonville, fl': 'America/New_York',
  'miami, fl': 'America/New_York',
  'orlando, fl': 'America/New_York',
  'tampa, fl': 'America/New_York',
  
  // Texas cities
  'houston, tx': 'America/Chicago',
  'dallas, tx': 'America/Chicago',
  'austin, tx': 'America/Chicago',
  'san antonio, tx': 'America/Chicago',
  'el paso, tx': 'America/Denver', // Mountain Time
  
  // North Dakota - western part uses Mountain Time
  'dickinson, nd': 'America/Denver',
  'williston, nd': 'America/Denver',
  
  // Kentucky - eastern part uses Eastern Time
  'pikeville, ky': 'America/New_York',
  'hazard, ky': 'America/New_York',
  
  // Tennessee - eastern part uses Eastern Time
  'knoxville, tn': 'America/New_York',
  'chattanooga, tn': 'America/New_York',
  'nashville, tn': 'America/Chicago',
  'memphis, tn': 'America/Chicago',
  
  // Michigan - most uses Eastern, but some western areas use Central
  'detroit, mi': 'America/Detroit',
  'grand rapids, mi': 'America/Detroit',
  'lansing, mi': 'America/Detroit',
  'iron mountain, mi': 'America/Chicago', // Upper Peninsula western counties
  'ironwood, mi': 'America/Chicago',
  
  // Kansas - small western area uses Mountain Time
  'goodland, ks': 'America/Denver',
  'sharon springs, ks': 'America/Denver',
  
  // Nebraska - small western area uses Mountain Time  
  'scottsbluff, ne': 'America/Denver',
  'sidney, ne': 'America/Denver',
  
  // Oregon - small eastern area uses Mountain Time
  'ontario, or': 'America/Denver',
  'vale, or': 'America/Denver',
  
  // Idaho - northern panhandle uses Pacific Time
  'coeur d\'alene, id': 'America/Los_Angeles',
  'sandpoint, id': 'America/Los_Angeles',
  'moscow, id': 'America/Los_Angeles',
  'lewiston, id': 'America/Los_Angeles',
};

/**
 * Detects timezone based on city and state
 * @param city - City name (case insensitive)
 * @param state - State abbreviation or full name (case insensitive)
 * @returns IANA timezone identifier
 */
export function detectTimezone(city: string | null, state: string | null): string {
  // Default fallback
  const DEFAULT_TIMEZONE = 'America/New_York';
  
  if (!state) {
    return DEFAULT_TIMEZONE;
  }
  
  // Normalize state to uppercase abbreviation
  const normalizedState = normalizeState(state);
  
  // Try city/state combination first
  if (city && normalizedState) {
    const cityStateKey = `${city.toLowerCase().trim()}, ${normalizedState.toLowerCase()}`;
    const cityTimezone = CITY_STATE_TIMEZONE_MAP[cityStateKey];
    if (cityTimezone) {
      return cityTimezone;
    }
  }
  
  // Fallback to state-level default
  if (normalizedState) {
    const stateTimezone = STATE_TIMEZONE_DEFAULTS[normalizedState];
    if (stateTimezone) {
      return stateTimezone;
    }
  }
  
  return DEFAULT_TIMEZONE;
}

/**
 * Normalizes state name to standard 2-letter abbreviation
 */
function normalizeState(state: string): string {
  const normalized = state.trim().toUpperCase();
  
  // If already 2 letters, return as-is
  if (normalized.length === 2) {
    return normalized;
  }
  
  // Common full state name mappings
  const STATE_NAME_MAP: Record<string, string> = {
    'ALABAMA': 'AL',
    'ALASKA': 'AK', 
    'ARIZONA': 'AZ',
    'ARKANSAS': 'AR',
    'CALIFORNIA': 'CA',
    'COLORADO': 'CO',
    'CONNECTICUT': 'CT',
    'DELAWARE': 'DE',
    'FLORIDA': 'FL',
    'GEORGIA': 'GA',
    'HAWAII': 'HI',
    'IDAHO': 'ID',
    'ILLINOIS': 'IL',
    'INDIANA': 'IN',
    'IOWA': 'IA',
    'KANSAS': 'KS',
    'KENTUCKY': 'KY',
    'LOUISIANA': 'LA',
    'MAINE': 'ME',
    'MARYLAND': 'MD',
    'MASSACHUSETTS': 'MA',
    'MICHIGAN': 'MI',
    'MINNESOTA': 'MN',
    'MISSISSIPPI': 'MS',
    'MISSOURI': 'MO',
    'MONTANA': 'MT',
    'NEBRASKA': 'NE',
    'NEVADA': 'NV',
    'NEW HAMPSHIRE': 'NH',
    'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM',
    'NEW YORK': 'NY',
    'NORTH CAROLINA': 'NC',
    'NORTH DAKOTA': 'ND',
    'OHIO': 'OH',
    'OKLAHOMA': 'OK',
    'OREGON': 'OR',
    'PENNSYLVANIA': 'PA',
    'RHODE ISLAND': 'RI',
    'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD',
    'TENNESSEE': 'TN',
    'TEXAS': 'TX',
    'UTAH': 'UT',
    'VERMONT': 'VT',
    'VIRGINIA': 'VA',
    'WASHINGTON': 'WA',
    'WEST VIRGINIA': 'WV',
    'WISCONSIN': 'WI',
    'WYOMING': 'WY',
    'DISTRICT OF COLUMBIA': 'DC',
  };
  
  return STATE_NAME_MAP[normalized] || normalized;
}

/**
 * Gets current time in a specific timezone
 * @param timezone IANA timezone identifier
 * @returns Date object representing current time in that timezone
 */
export function getCurrentTimeInTimezone(timezone: string): Date {
  return new Date(new Date().toLocaleString("en-US", { timeZone: timezone }));
}

/**
 * Converts a time to a specific timezone
 * @param date Date to convert
 * @param timezone Target timezone
 * @returns Date object in target timezone
 */
export function convertToTimezone(date: Date, timezone: string): Date {
  return new Date(date.toLocaleString("en-US", { timeZone: timezone }));
}

/**
 * Formats a date for the current timezone context
 * @param date Date to format
 * @returns YYYY-MM-DD string
 */
export function formatDateForSchedule(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Gets the day of week in lowercase (for schedule matching)
 * @param date Date object
 * @returns Day name in lowercase (e.g., 'monday', 'tuesday')
 */
export function getDayOfWeek(date: Date): string {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
}